import { useForm } from "@inertiajs/react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight } from "lucide-react";

interface User {
  email: string;
  age: number | null;
  gender: string | null;
}

interface Props {
  user: User;
  errors?: Record<string, string | string[]>;
}

export default function Edit({ user, errors }: Props) {
  const { data, setData, patch, processing } = useForm({
    age: user.age?.toString() ?? "",
    gender: user.gender ?? "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch("/profile");
  };

  const typedErrors = errors as Record<string, string | undefined>;

  return (
    <Layout title="Perfil" user={user}>
      <div className="mx-auto max-w-lg px-4 py-10">
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-2xl">Seu Perfil</CardTitle>
            <CardDescription>
              Atualize sua idade e gênero para receber fotos compatíveis.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="age" className="text-foreground">
                  Idade
                </Label>
                <Input
                  id="age"
                  type="number"
                  min={18}
                  max={120}
                  value={data.age}
                  onChange={(e) => setData("age", e.target.value)}
                  placeholder="Digite sua idade"
                  className="border-input bg-background text-foreground placeholder:text-muted-foreground"
                />
                {typedErrors?.age && <p className="text-sm text-red-400">{typedErrors.age}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender" className="text-foreground">
                  Gênero
                </Label>
                <Select value={data.gender} onValueChange={(value) => setData("gender", value)}>
                  <SelectTrigger className="border-input bg-background text-foreground">
                    <SelectValue placeholder="Selecione seu gênero" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-card">
                    <SelectItem value="male">Masculino</SelectItem>
                    <SelectItem value="female">Feminino</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
                {typedErrors?.gender && (
                  <p className="text-sm text-red-400">{typedErrors.gender}</p>
                )}
              </div>

              <Button type="submit" disabled={processing} className="w-full gap-2">
                {processing ? "Salvando..." : "Salvar Perfil"}
                <ArrowRight size={16} />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
